import { stateUSA } from '../constants/state';
import { genderOptions, stateOptions } from '../constants';

export const Initials = (str) => {
  return str
    .split(/\s+/)
    .map((w) => w.substring(0, 1).toUpperCase())
    .join('');
};

export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

export const getState = (stateId) => {
  if (stateId.length > 2) {
    return stateUSA.find((item) => item.label === stateId);
  } else {
    return stateUSA.find((item) => item.value === stateId);
  }
};

export const redirectToLibrary = (navigate) => {
  navigate('/corporate-library');
};

export const getAllDaysInMonth = (month, year) =>
  Array.from({ length: new Date(year, month, 0).getDate() }, (_, i) => new Date(year, month - 1, i + 1));

export const extractBedParts = (input) => {
  if (!input) return { number: '', letter: '' };

  const match = input.match(/^([\d().'’]+)(.*)$/);
  return {
    number: match ? match[1] : '',
    letter: match ? match[2] : '',
  };
};

export const formatTaskPeriodSettings = (periodSettings) => {
  if (!periodSettings) {
    return '';
  }

  const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const quantityWordsMap = ['', '', '2nd ', '3rd ', '4th ', '5th '];

  if (periodSettings.period_type == 'week_days') {
    return `Every ${periodSettings.selected_weekdays
      .sort((a, b) => a - b)
      .map((index) => weekdayNames[index])
      .join(', ')}`;
  } else if (periodSettings.period_type == 'custom_dates') {
    return `Every month on ${periodSettings.custom_dates.join(', ')}`;
  } else if (periodSettings.period_type == 'custom_interval') {
    return `Every ${quantityWordsMap[periodSettings.repeat_quantity]} ${weekdayNames[periodSettings.repeat_day]}`;
  }

  return '';
};

export const convertTimeRangeTo12Hour = (timeRange) => {
  // Convert time range 7-15 to 7-3, 23-7 to 11-7 etc.

  if (!timeRange) {
    return '';
  }
  const [startStr, endStr] = timeRange.split('-');
  const start = parseInt(startStr, 10) % 12;
  const end = parseInt(endStr, 10) % 12;

  return `${start}-${end}`;
};

export const getDateFromString = (dateStr) => {
  // string format: YYYY-MM-DD
  return (() => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  })();
};

export const replaceValuesInText = (text, autoFillingVariables, submitterType, submitterInfo, replaceWithEmpty = false) => {
  if ((!text || !autoFillingVariables || !submitterType || !submitterInfo) && !replaceWithEmpty) {
    return text;
  }

  const replacedText = text.replace(/{([^}]+)}/g, (match, variable_name) => {
    if (replaceWithEmpty) {
      return '';
    }

    const cleanVariable = variable_name
      .replaceAll(/&nbsp;/g, ' ')
      .replaceAll(' ', ' ')
      .toLowerCase();

    const variable = autoFillingVariables.find((variable) => {
      const variableLower = variable.name
        .replaceAll(/&nbsp;/g, ' ')
        .replaceAll(' ', ' ')
        .toLowerCase();

      return (
        variableLower == cleanVariable &&
        (variable.user_type === submitterType ||
          variable.user_type === 'other' ||
          (variable.user_type === 'resident' && submitterType === 'representative') ||
          (variable.user_type === 'representative' && submitterType === 'resident'))
      );
    });

    if (variable) {
      if (variable.user_type === 'resident') {
        switch (variable.info_type) {
          case 'name':
            return submitterInfo.resident.first_name || '';
          case 'last_name':
            return submitterInfo.resident.last_name || '';
          case 'gender':
            return genderOptions.find((item) => item.value === submitterInfo.resident.gender)?.label || '';
          case 'date_of_birth':
            return submitterInfo.resident.date_of_birth || '';
          case 'SSN':
            return submitterInfo.resident.ssn || '';
          case 'phone_number':
            return submitterInfo.resident.phone_number || '';
          case 'email':
            return submitterInfo.resident.email || '';
          case 'address':
            return submitterInfo.resident.address || '';
          case 'address_2':
            return submitterInfo.resident.address_2 || '';
          case 'city':
            return submitterInfo.resident.city || '';
          case 'state':
            return stateOptions.find((item) => item.value === submitterInfo.resident.state)?.label || '';
          case 'zip':
            return submitterInfo.resident.zip_code || '';
          case 'facility_name':
            return submitterInfo.resident.facility_name || '';
          case 'unit_name':
            return submitterInfo.resident.unit_name || '';
          case 'physician_name':
            return submitterInfo.resident.physician_name || '';
          case 'medical_record_number':
            return submitterInfo.resident.medical_record_number || '';
          case 'date_of_admission':
            return submitterInfo.resident.date_of_admission || '';
          case 'room_number':
            return submitterInfo.resident.room_number || '';
          case 'date':
            return new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
        }
      }

      if (variable.user_type === 'representative') {
        switch (variable.info_type) {
          case 'name':
            return submitterInfo.representative?.first_name || '';
          case 'last_name':
            return submitterInfo.representative?.last_name || '';
          case 'phone_number':
            return submitterInfo.representative?.phone_number || '';
          case 'email':
            return submitterInfo.representative?.email || '';
          case 'relation':
            return submitterInfo.representative?.relation || '';
        }
      }

      if (variable.user_type === 'other') {
        switch (variable.info_type) {
          case 'date':
            return new Date().toLocaleDateString('en-US', {
              day: 'numeric',
            });
          case 'month':
            return new Date().toLocaleDateString('en-US', {
              month: 'long',
            });
          case 'year':
            return new Date().getFullYear();
        }
      }

      if (variable.user_type === 'employee') {
        switch (variable.info_type) {
          case 'name':
            return submitterInfo.first_name;
          case 'last_name':
            return submitterInfo.last_name;
          case 'position':
            return submitterInfo.position || '';
        }
      }
    }

    return '';
  });

  return replacedText;
};
